import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../components/Layout/Layout';
import api from '../services/api';
import './FAQManager.css';

const LANGUAGES = [
  { value: 'en', label: 'English' },
  { value: 'ms', label: 'Bahasa Melayu' },
  { value: 'ar', label: 'Arabic' },
  { value: 'zh', label: 'Chinese' },
  { value: 'ja', label: 'Japanese' },
];

const EMPTY_FORM = {
  question: '',
  answer: '',
  category: '',
  language: 'en',
  sort_order: 0,
  status: 'active',
};

const FAQManager = () => {
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingFaq, setEditingFaq] = useState(null);
  const [formData, setFormData] = useState({ ...EMPTY_FORM });

  const fetchFaqs = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const response = await api.get('/api/vmai/faqs');
      const data = Array.isArray(response.data) ? response.data : response.data?.data || [];
      setFaqs(data);
    } catch (err) {
      console.error('Error fetching FAQs:', err);
      setError('Failed to load FAQs');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchFaqs();
  }, [fetchFaqs]);

  const clearMessages = () => {
    setError('');
    setSuccess('');
  };

  const openCreateModal = () => {
    clearMessages();
    setEditingFaq(null);
    setFormData({ ...EMPTY_FORM });
    setShowModal(true);
  };

  const openEditModal = (faq) => {
    clearMessages();
    setEditingFaq(faq);
    setFormData({
      question: faq.question || '',
      answer: faq.answer || '',
      category: faq.category || '',
      language: faq.language || 'en',
      sort_order: faq.sort_order ?? 0,
      status: faq.status || 'active',
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingFaq(null);
    setFormData({ ...EMPTY_FORM });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: name === 'sort_order' ? parseInt(value, 10) || 0 : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    clearMessages();

    if (!formData.question.trim() || !formData.answer.trim()) {
      setError('Question and Answer are required');
      return;
    }

    try {
      if (editingFaq) {
        await api.put(`/api/vmai/faqs/${editingFaq.id}`, formData);
        setSuccess('FAQ updated successfully');
      } else {
        await api.post('/api/vmai/faqs', formData);
        setSuccess('FAQ created successfully');
      }
      closeModal();
      fetchFaqs();
    } catch (err) {
      console.error('Error saving FAQ:', err);
      setError(err.response?.data?.message || 'Failed to save FAQ');
    }
  };

  const handleDelete = async (faq) => {
    if (!window.confirm(`Delete FAQ: "${faq.question.substring(0, 60)}..."?`)) return;
    clearMessages();

    try {
      await api.delete(`/api/vmai/faqs/${faq.id}`);
      setSuccess('FAQ deleted successfully');
      fetchFaqs();
    } catch (err) {
      console.error('Error deleting FAQ:', err);
      setError('Failed to delete FAQ');
    }
  };

  const toggleStatus = async (faq) => {
    clearMessages();
    const newStatus = faq.status === 'active' ? 'inactive' : 'active';
    try {
      await api.put(`/api/vmai/faqs/${faq.id}`, { status: newStatus });
      setSuccess(`FAQ ${newStatus === 'active' ? 'activated' : 'deactivated'}`);
      fetchFaqs();
    } catch (err) {
      console.error('Error toggling FAQ status:', err);
      setError('Failed to update FAQ status');
    }
  };

  const getLangLabel = (code) => LANGUAGES.find((l) => l.value === code)?.label || code;

  if (loading) {
    return (
      <Layout>
        <div className="faq-manager">
          <div className="loading">
            <div className="spinner"></div>
            <p>Loading FAQs...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="faq-manager">
        {/* Header */}
        <div className="faq-header">
          <div>
            <h1>FAQ Manager</h1>
            <p>Manage frequently asked questions for the chatbot</p>
          </div>
          <button className="btn btn-primary" onClick={openCreateModal}>
            + Add FAQ
          </button>
        </div>

        {/* Messages */}
        {error && <div className="alert alert-error">{error}</div>}
        {success && <div className="alert alert-success">{success}</div>}

        {/* Stats */}
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-icon">📋</div>
            <div className="stat-content">
              <h3>Total FAQs</h3>
              <p className="stat-number">{faqs.length}</p>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">✅</div>
            <div className="stat-content">
              <h3>Active</h3>
              <p className="stat-number">{faqs.filter((f) => f.status === 'active').length}</p>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">⏸️</div>
            <div className="stat-content">
              <h3>Inactive</h3>
              <p className="stat-number">{faqs.filter((f) => f.status === 'inactive').length}</p>
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="faq-table-container">
          <table className="faq-table">
            <thead>
              <tr>
                <th>Order</th>
                <th>Question</th>
                <th>Category</th>
                <th>Language</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {faqs.length === 0 ? (
                <tr>
                  <td colSpan="6" className="empty-state">No FAQs found. Click "+ Add FAQ" to create one.</td>
                </tr>
              ) : (
                faqs.map((faq) => (
                  <tr key={faq.id}>
                    <td>{faq.sort_order}</td>
                    <td className="question-cell">{faq.question}</td>
                    <td>{faq.category || '—'}</td>
                    <td>{getLangLabel(faq.language)}</td>
                    <td>
                      <span
                        className={`status-badge ${faq.status === 'active' ? 'status-active' : 'status-inactive'}`}
                        onClick={() => toggleStatus(faq)}
                        title="Click to toggle"
                        style={{ cursor: 'pointer' }}
                      >
                        {faq.status}
                      </span>
                    </td>
                    <td className="actions-cell">
                      <button className="btn btn-sm btn-edit" onClick={() => openEditModal(faq)}>Edit</button>
                      <button className="btn btn-sm btn-delete" onClick={() => handleDelete(faq)}>Delete</button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Create / Edit Modal */}
        {showModal && (
          <div className="modal-overlay" onClick={closeModal}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>{editingFaq ? 'Edit FAQ' : 'Create FAQ'}</h2>
                <button className="modal-close" onClick={closeModal}>&times;</button>
              </div>
              <form onSubmit={handleSubmit}>
                <div className="form-group">
                  <label>Question *</label>
                  <textarea
                    name="question"
                    value={formData.question}
                    onChange={handleChange}
                    rows={3}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Answer *</label>
                  <textarea
                    name="answer"
                    value={formData.answer}
                    onChange={handleChange}
                    rows={5}
                    required
                  />
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Category</label>
                    <input
                      type="text"
                      name="category"
                      value={formData.category}
                      onChange={handleChange}
                      placeholder="e.g. General, Transport, Food"
                    />
                  </div>
                  <div className="form-group">
                    <label>Language</label>
                    <select name="language" value={formData.language} onChange={handleChange}>
                      {LANGUAGES.map((lang) => (
                        <option key={lang.value} value={lang.value}>{lang.label}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Sort Order</label>
                    <input
                      type="number"
                      name="sort_order"
                      value={formData.sort_order}
                      onChange={handleChange}
                      min={0}
                    />
                  </div>
                  <div className="form-group">
                    <label>Status</label>
                    <select name="status" value={formData.status} onChange={handleChange}>
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                    </select>
                  </div>
                </div>
                <div className="modal-actions">
                  <button type="button" className="btn btn-secondary" onClick={closeModal}>Cancel</button>
                  <button type="submit" className="btn btn-primary">
                    {editingFaq ? 'Update FAQ' : 'Create FAQ'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default FAQManager;
